'use client';

import { useCallback, useMemo } from 'react';
import { useSearchParams, usePathname } from 'next/navigation';
import dynamic from 'next/dynamic';
import { observer } from 'mobx-react-lite';

import { deleteUrlQueryParam, isMobile, updateUrlQueryParam } from 'utils/misc';
import { listPredefinedDates } from 'constants/listPredefinedDates';
import { urlParamsSearch } from 'constants/search';

import { OnSelectFilterParam } from 'ParamsResult';
import { filterParams } from './filterOptions';

import FilterWrapper from './components/FilterWrapper';

import * as styles from './SearchFilter.css';

const SelectModal = dynamic(() => import('@sympla/sympla-components/SelectModal'), { ssr: false });
const CalendarModal = dynamic(() => import('@sympla/sympla-components/CalendarModal'), { ssr: false });

type Params = {
  fetchData: (query: string) => void;
  isSpaceBetween?: boolean;
  activeFilters?: {
    collections: boolean,
    price: boolean,
    date: boolean,
    type: boolean,
  }
};

function SearchFilter({
  fetchData,
  isSpaceBetween,
  activeFilters = {
    collections: true,
    price: true,
    date: true,
    type: true,
  },
}: Params) {
  const searchParams = useSearchParams();

  const pathname = usePathname();
  const hideDateFilter = useMemo(() => {
    if (!activeFilters?.date) return true;
    const partsSlugs = pathname?.split('/')?.pop();
    return listPredefinedDates.some((option) => option.dateId === partsSlugs);
  }, [pathname, activeFilters?.date]);

  const initialFilterState = useMemo(() => {
    const state = Object.entries(filterParams).map(([key, content]) => {
      const queryParam = searchParams?.get(content.name) as string | undefined;

      if (!queryParam) return [key, false];

      const value = content.options.find((op) => op?.value === queryParam) ?? false;

      return [key, value];
    });

    return Object.fromEntries(state);
  }, [searchParams]);

  const onSelectFilter = useCallback(
    (item: OnSelectFilterParam) => {
      deleteUrlQueryParam({ param: urlParamsSearch.page });
      if (item.value || item.keyword) {
        updateUrlQueryParam({ param: item.param || '', value: item.value || item.keyword });
      } else {
        deleteUrlQueryParam({ param: item.param || '' });
      }
      const query = window?.location?.search;
      return fetchData(query);
    },
    [searchParams],
  );

  return (
    <div className={`${styles.Container} ${isSpaceBetween ? styles.ContainerSpaceBetween : null}`} data-testid="search-filter-container">
      <div>
        <p className={styles.Title}>Filtrar por</p>
        <div className={`${styles.ButtonsWrapper} ${isSpaceBetween ? null : styles.ContentFilter}`} data-testid="search-filter-buttons-wrapper">
          {activeFilters?.collections && (
            <FilterWrapper
              label={filterParams.collections.trackingLabel}
              nameButton={filterParams.collections.label}
              getSelectedItem={(item: OnSelectFilterParam) => onSelectFilter({ ...item, param: filterParams.collections.name })}
              activeFilter={initialFilterState.collections}
              content={(
                <SelectModal
                  open={false}
                  border
                  title={filterParams.collections.title}
                  accessibilityTitle={filterParams.collections.title}
                  columns={{
                    xs: 1,
                    sm: 2,
                    md: 3,
                  }}
                  options={filterParams.collections.options}
                  hideModal={() => null}
                  widthModal={880}
                />
              )}
            />
          )}
          {hideDateFilter ? null : (
            <FilterWrapper
              label={filterParams.date.trackingLabel}
              nameButton={filterParams.date.label}
              activeFilter={initialFilterState.date}
              getSelectedItem={(item: OnSelectFilterParam) => onSelectFilter({ ...item, param: filterParams.date.name })}
              content={(
                <CalendarModal
                  open={false}
                  accessibilityTitle={filterParams.date.title}
                  title={filterParams.date.title}
                  hideModal={() => null}
                  isMobile={isMobile()}
                />
              )}
            />
          )}

          {activeFilters.price && (
            <FilterWrapper
              label={filterParams.price.trackingLabel}
              nameButton={filterParams.price.label}
              getSelectedItem={(item: OnSelectFilterParam) => onSelectFilter({ ...item, param: filterParams.price.name })}
              activeFilter={initialFilterState.price}
              content={(
                <SelectModal
                  open={false}
                  border
                  title={filterParams.price.title}
                  accessibilityTitle={filterParams.price.title}
                  columns={{
                    xs: 1,
                    sm: 1,
                    md: 1,
                  }}
                  options={filterParams.price.options}
                  hideModal={() => null}
                />
              )}
            />
          )}
          {activeFilters.type && (
            <FilterWrapper
              label={filterParams.type.trackingLabel}
              nameButton={filterParams.type.label}
              activeFilter={initialFilterState.type}
              getSelectedItem={(item: OnSelectFilterParam) => onSelectFilter({ ...item, param: filterParams.type.name })}
              content={(
                <SelectModal
                  open={false}
                  border
                  title={filterParams.type.title}
                  accessibilityTitle={filterParams.type.title}
                  columns={{
                    xs: 1,
                    sm: 1,
                    md: 1,
                  }}
                  options={filterParams.type.options}
                  hideModal={() => null}
                />
              )}
            />
          )}
        </div>
      </div>
      <div>
        <p className={styles.Title}>Ordenar por</p>
        <div className={styles.ButtonsWrapper}>
          <FilterWrapper
            label={filterParams.sort.trackingLabel}
            nameButton={filterParams.sort.options[0].label}
            activeFilter={initialFilterState.sort}
            getSelectedItem={(item: OnSelectFilterParam) => onSelectFilter({ ...item, param: filterParams.sort.name })}
            content={(
              <SelectModal
                open={false}
                border
                title={filterParams.sort.title}
                accessibilityTitle={filterParams.sort.title}
                columns={{
                  xs: 1,
                  sm: 1,
                  md: 1,
                }}
                options={filterParams.sort.options}
                hideModal={() => null}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
}

export default observer(SearchFilter);
